import React, { useEffect, useState } from "react";
import {
    FormControl,
    FormControlLabel,
    FormHelperText,
    Stack,
    Avatar,
    Box,
    Checkbox,
    Grid,
    Button,
    TextField,
    Container,
    Alert,
    Link,
} from "@mui/material";
import ImageUploading, { ImageType } from "react-images-uploading";
import { InfinitySpin } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomSnackbar from "../../common/components/CustomSnackbar";
import PasswordInput from "../../common/components/PasswordInput";
import { useAppDispatch, useAppSelector } from "../../common/redux/reduxHooks";
import { RootState } from "../../common/redux/store";
import { RouteAPI } from "../../common/service/apiService";
import {
    AvatarResponse,
    UserStructureResponse,
    emptyUserStructure,
} from "../../structures/model/StructureModel";
import { postAvatar, signUp } from "../../structures/redux/signUpAction";
import { clearPicture } from "../../structures/redux/signUpSlice";
import { getPictureFromProfilImage } from "../../utils/FormatUtils";
import { language, LanguageType } from "../../utils/LanguageUtils";
import { RouteUtils } from "../../utils/RouteUtils";
import { SignUpData } from "../../login/model/SignUpData";
import { RouteApp } from "../../common/model/RouteApp";
import { Modal } from "../../common/components/Modal";
import { Country } from "../redux/countryActions";
import CountrySelect from "./CountryInput";
import SiretInput from "./SiretInput";
import CityInput from "./CityInput";

export default function StructureForm() {
    const [snackBarMessage, setSnackBarMessage] = useState<any | undefined>(
        undefined
    );
    const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
    const [isSuccesSignUp, setIsSuccesSignUp] = useState<boolean>(false);
    const [avatar, setAvatar] = useState<AvatarResponse>({
        contentUrl: "",
        id: "",
    });
    const [image, setImage] = useState<any>([]);
    const [displayAvatar, setDisplayAvatar] = useState<boolean>(false);
    const [legacyChecked, setLegacyChecked] = useState<boolean>(false);
    const [errorLegacy, setErrorLegacy] = useState<boolean>(false);
    const [userInfos, setUserInfos] =
        useState<UserStructureResponse>(emptyUserStructure);
    const [isLocatedInFrance, setIsLocatedInFrance] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(
        null
    );
    const [selectedCity, setSelectedCity] = useState<string | null>(null);
    const [siretValue, setSiretValue] = useState<string>("");
    const [siretValidated, setSiretValidated] = useState<boolean>(false);
    const [passwordValidated, setPasswordValidated] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const initAvatar = { contentUrl: "", id: "" };
    const structureSelector = useAppSelector(
        (state: RootState) => state.signUp
    );
    const { t } = useTranslation();
    const lang: LanguageType = language();
    const navigate = useNavigate();

    function handleCloseSnackbar(
        event?: React.SyntheticEvent | Event,
        reason?: string
    ) {
        setSnackBarMessage(undefined);
    }

    const onSuccesSignUp = () => {
        navigate(RouteUtils.LOGIN);
    };

    const handleSubmit = (event: any) => {
        event.preventDefault();
        if (legacyChecked !== true) {
            setErrorLegacy(true);
            return;
        }
        setErrorLegacy(false);
        const form = new FormData(event.currentTarget);
        let data = {
            email: form.get("email") as string,
            plainPassword: form.get("password") as string,
            telephone: form.get("phone") as string,
            siret: siretValue,
            societe: form.get("societe") as string,
            description: form.get("societe") as string,
            ville: selectedCity,
            country: selectedCountry?.id
                ? `${RouteAPI.COUNTRY}/${selectedCountry.id}`
                : "",
        };

        if (isLocatedInFrance) {
            data.country = "/api/countries/1";
            data.siret = form.get("siret") as string;
            if (!siretValidated) {
                setSnackBarMessage({
                    detail: t("error.siret.validRequired"),
                    status: 400,
                });
                return;
            }
        }

        if (avatar.id.length > 0)
            Object.assign(
                data,
                Object(avatar.id.length) > 1 && {
                    profileImage: RouteAPI.GET_MEDIA_OBJECTS_ID + avatar.id,
                }
            );

        if (!passwordValidated) {
            setSnackBarMessage({
                detail: t("error.password.requirements"),
                status: 400,
            });
            return;
        }

        if (!data.email) {
            setSnackBarMessage({
                detail: t("profileForm.validation.email"),
                status: 400,
            });
            return;
        }

        if (!data.telephone) {
            setSnackBarMessage({
                detail: t("profileForm.validation.phone"),
                status: 400,
            });
            return;
        }

        if (!data.societe) {
            setSnackBarMessage({
                detail: t("profileForm.validation.organization"),
                status: 400,
            });
            return;
        }

        if (!data.country) {
            setSnackBarMessage({
                detail: t("profileForm.validation.country"),
                status: 400,
            });
            return;
        }

        if (!data.ville) {
            setSnackBarMessage({
                detail: t("profileForm.validation.city"),
                status: 400,
            });
            return;
        }

        dispatch(signUp(data as SignUpData));
    };

    const deleteAvatar = () => {
        setAvatar(initAvatar);
        dispatch(clearPicture());
        setDisplayAvatar(false);
    };

    const onChange = (image: ImageType) => {
        if (image[0]) {
            dispatch(postAvatar(image[0].file));
            setImage(image);
            setDisplayAvatar(true);
        }
    };

    useEffect(() => {
        if (structureSelector.data.profileImage) {
            setAvatar(structureSelector.data.profileImage);
        }
        if (structureSelector.error) {
            setSnackBarMessage(structureSelector.error.response?.data);
            setIsSnackBarOpen(true);
        }
        if (structureSelector.data) {
            setUserInfos(structureSelector.data);
        }
        return setIsSnackBarOpen(false);
    }, [structureSelector]);

    useEffect(() => {
        if (userInfos.id) setIsSuccesSignUp(true);
    }, [userInfos]);

    useEffect(() => {
        setIsSnackBarOpen(false);
        setSnackBarMessage(undefined);
    }, []);

    const legacyCheckboxLink = () => {
        return (
            <span onClick={() => navigate(RouteApp.LEGACY)}>
                <u>{t("sign.up.condition")}</u>
            </span>
        );
    };

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Box
                    component="form"
                    noValidate
                    onSubmit={handleSubmit}
                    sx={{ mt: 2 }}
                >
                    <Box sx={{ mb: 2 }}>
                        <ImageUploading
                            value={image}
                            onChange={(img) => onChange(img)}
                        >
                            {({
                                onImageUpload,
                                onImageUpdate,
                                isDragging,
                                dragProps,
                            }) => (
                                <Stack className="upload__image-wrapper">
                                    <Button
                                        variant="outlined"
                                        sx={{ alignSelf: "center" }}
                                        style={
                                            isDragging
                                                ? { color: "red" }
                                                : undefined
                                        }
                                        onClick={onImageUpload}
                                        {...dragProps}
                                    >
                                        {t("sign.up.avatar")}
                                    </Button>
                                    {displayAvatar &&
                                        avatar.contentUrl.length > 0 && (
                                            <div
                                                className="image-item"
                                                style={{
                                                    textAlign: "center",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <Avatar
                                                    src={
                                                        avatar.contentUrl
                                                            ? getPictureFromProfilImage(
                                                                  avatar.contentUrl
                                                              )
                                                            : undefined
                                                    }
                                                    sx={{
                                                        width: 94,
                                                        height: 94,
                                                        m: 1,
                                                    }}
                                                    variant="square"
                                                    sizes="large"
                                                />
                                                <div className="image-item__btn-wrapper">
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() =>
                                                            onImageUpdate(0)
                                                        }
                                                        sx={{ mr: 1 }}
                                                    >
                                                        {t(
                                                            "sign.up.avatar.button.modify"
                                                        )}
                                                    </Button>
                                                    <Button
                                                        sx={{ px: 2.8 }}
                                                        variant="outlined"
                                                        onClick={() =>
                                                            deleteAvatar()
                                                        }
                                                    >
                                                        {t(
                                                            "sign.up.avatar.button.delete"
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    {structureSelector.isExtraField &&
                                        avatar.contentUrl.length <= 0 && (
                                            <Box sx={{ alignSelf: "center" }}>
                                                <InfinitySpin
                                                    color="#A93F58"
                                                    width="200"
                                                />
                                            </Box>
                                        )}
                                </Stack>
                            )}
                        </ImageUploading>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!isLocatedInFrance}
                                    onChange={() =>
                                        setIsLocatedInFrance(!isLocatedInFrance)
                                    }
                                    color="primary"
                                    name="international"
                                />
                            }
                            label={t("sign.up.checkbox")}
                        />
                    </Box>

                    {!isLocatedInFrance && (
                        <Box sx={{ mb: 1 }}>
                            <CountrySelect
                                label={t("sign.up.country")}
                                value={selectedCountry}
                                disabledCountryCodes={["FR"]}
                                onChange={(country) =>
                                    setSelectedCountry(country)
                                }
                                name="country"
                            />
                        </Box>
                    )}

                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <CityInput
                                value={selectedCity}
                                onChange={(city) => setSelectedCity(city)}
                                countryCode={
                                    isLocatedInFrance
                                        ? "fr"
                                        : selectedCountry?.code
                                }
                                countryName={
                                    isLocatedInFrance
                                        ? "France"
                                        : selectedCountry?.name
                                }
                                name="city"
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <TextField
                                autoComplete="organization"
                                name="societe"
                                required
                                fullWidth
                                id="societe"
                                label={t("sign.up.organization")}
                                autoFocus
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            {isLocatedInFrance && (
                                <Grid item xs={12} sm={12}>
                                    <SiretInput
                                        value={siretValue}
                                        onChange={setSiretValue}
                                        onValidate={setSiretValidated}
                                        label={t("sign.up.siret")}
                                        required
                                    />
                                </Grid>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                type="number"
                                id="phone"
                                label={t("sign.up.phone")}
                                name="phone"
                                autoComplete="tel-national"
                                sx={{
                                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                        {
                                            display: "none",
                                        },
                                    "& input[type=number]": {
                                        MozAppearance: "textfield",
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                id="email"
                                label={t("auth.email")}
                                name="email"
                                autoComplete="email"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <PasswordInput
                                validationEnabled={true}
                                onValidate={setPasswordValidated}
                            />
                        </Grid>
                    </Grid>
                    <FormControl
                        required
                        error={errorLegacy}
                        component="fieldset"
                        sx={{ mt: 3 }}
                        variant="standard"
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={legacyChecked}
                                    onChange={() =>
                                        setLegacyChecked(!legacyChecked)
                                    }
                                    name="terms"
                                />
                            }
                            label={
                                <>
                                    <span>{t("sign.up.therms")}</span>{" "}
                                    {legacyCheckboxLink()}
                                </>
                            }
                        />
                        {errorLegacy && (
                            <FormHelperText>
                                {t("sign.up.error")}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        color="error"
                    >
                        {t("sign.up.button.register")}
                    </Button>
                    <CustomSnackbar
                        open={isSnackBarOpen}
                        handleCloseSnackbar={handleCloseSnackbar}
                        message={
                            snackBarMessage?.detail
                                ? snackBarMessage?.detail
                                : ""
                        }
                        severity={
                            snackBarMessage?.status === 200
                                ? "success"
                                : "error"
                        }
                        position={{ vertical: "bottom", horizontal: "center" }}
                        autoHideDuration={
                            snackBarMessage?.status === 200 ? 3000 : 6000
                        }
                    />
                    {isSnackBarOpen || snackBarMessage ? (
                        <Alert severity="error" className="errorMessage">
                            {snackBarMessage?.detail
                                ? snackBarMessage?.detail
                                : ""}
                        </Alert>
                    ) : null}
                    <Grid
                        container
                        justifyContent="flex-end"
                        marginBottom="1rem"
                    >
                        <Grid item>
                            <Link href={RouteUtils.LOGIN} variant="body2">
                                {t("sign.up.connection")}
                            </Link>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            <Modal
                open={isSuccesSignUp}
                title={lang.titleModalSignUpSuccess}
                message={lang.messageModalSignUpSucces}
                onClose={() => onSuccesSignUp()}
                onValidation={() => onSuccesSignUp()}
                titleButtonValidation={"Valider"}
            />
        </Container>
    );
}
